import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SuiDialogBase } from '@sui/dialog';
import { DialogCloseReason } from '@sui/dialog';

export interface ConfirmOption {
  key: string;
  label: string;
  value: boolean;
}

import { RecurrenceDefinition } from '../../../models/schedule.models';
export interface ConfirmDialogData {
  title?: string;
  message?: string;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  options?: ConfirmOption[];
  canSchedule?: boolean;
  initialSchedule?: boolean;
  editMode?: boolean;
  initialValues?: Record<string, any>;
}

export interface ConfirmDialogOutput {
  options?: ConfirmOption[];
  schedule?: boolean;
  schedule_recurrence?: RecurrenceDefinition;
  schedule_timezone_id?: number | null;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: false,
  templateUrl: './confirm-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './confirm-dialog.component.scss',
})
export class ConfirmDialogComponent extends SuiDialogBase<
  ConfirmDialogData,
  ConfirmDialogOutput
> {
  private translateService = inject(TranslateService);

  defaultTitle = this.translateService.instant('confirm-dialog.default-title');
  defaultMessage = this.translateService.instant(
    'confirm-dialog.default-message',
  );

  scheduleForm = new FormGroup({});

  private scheduleFormValue = toSignal(this.scheduleForm.valueChanges, {
    initialValue: this.scheduleForm.value as { schedule?: boolean },
  });

  isScheduleMode = computed(
    () => !!(this.scheduleFormValue() as { schedule?: boolean })?.schedule,
  );

  confirmButtonText = computed(() =>
    this.canSchedule && this.isScheduleMode()
      ? this.translateService.instant('btn.schedule')
      : this.canSchedule
        ? this.translateService.instant('btn.run-now')
        : this.translateService.instant('btn.confirm'),
  );

  confirmButtonIcon = computed<IconProp | undefined>(() => {
    if (!this.canSchedule) return undefined;
    return this.isScheduleMode() ? ['fas', 'clock'] : ['fas', 'play'];
  });

  get title(): string {
    return this.dialogData.title ?? this.defaultTitle;
  }

  get message(): string {
    return this.dialogData.message ?? this.defaultMessage;
  }

  get hideCancelButton(): boolean {
    return this.dialogData.hideCancelButton ?? false;
  }

  get hideConfirmButton(): boolean {
    return this.dialogData.hideConfirmButton ?? false;
  }

  get options(): ConfirmOption[] {
    return this.dialogData.options ?? [];
  }

  get canSchedule(): boolean {
    return !!this.dialogData.canSchedule;
  }

  get initialSchedule(): boolean {
    return !!this.dialogData.initialSchedule;
  }

  get editMode(): boolean {
    return !!this.dialogData.editMode;
  }

  get initialRecurrence() {
    return this.dialogData.initialValues?.['schedule_recurrence'] ?? null;
  }

  get initialTimezoneId() {
    return this.dialogData.initialValues?.['schedule_timezone_id'] ?? null;
  }

  cancel(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }

  confirm(): void {
    const data: ConfirmDialogOutput = { options: this.options };
    if (this.canSchedule || this.editMode) {
      const value = this.scheduleForm.value as {
        schedule?: boolean;
        schedule_recurrence?: RecurrenceDefinition;
        schedule_timezone_id?: number | null;
      };
      data.schedule = !!value.schedule;
      if (value.schedule) {
        data.schedule_recurrence = value.schedule_recurrence;
        data.schedule_timezone_id = value.schedule_timezone_id;
      }
    }
    this.close({ reason: DialogCloseReason.Confirm, data });
  }
}
